// Location: frontend/src/admin/pages/UserManagement.jsx
// Fixed version - Works with object-based permissions

import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaUserShield, FaUserTie, FaUserEdit, FaToggleOn, FaToggleOff, FaTimes } from 'react-icons/fa';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4100";

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'hr_manager'
    });

    const [errors, setErrors] = useState({});

    // Role configuration
    const roleConfig = {
        admin: {
            label: 'Super Admin',
            icon: <FaUserShield />,
            color: 'text-red-600 bg-red-50 border-red-200',
            description: 'Full system access and control'
        },
        hr_manager: {
            label: 'HR Manager',
            icon: <FaUserTie />,
            color: 'text-blue-600 bg-blue-50 border-blue-200',
            description: 'Manage team members and careers'
        },
        seo_manager: {
            label: 'SEO Manager',
            icon: <FaUserEdit />,
            color: 'text-green-600 bg-green-50 border-green-200',
            description: 'Manage services, testimonials, about'
        },
        post_blogger: {
            label: 'Blogger',
            icon: <FaUserEdit />,
            color: 'text-purple-600 bg-purple-50 border-purple-200',
            description: 'Create and manage blog posts'
        }
    };

    // Helper function to get active permissions from object
    const getActivePermissions = (permissionsObj) => {
        if (!permissionsObj || typeof permissionsObj !== 'object') return [];

        return Object.entries(permissionsObj)
            .filter(([key, value]) => value === true)
            .map(([key]) => key);
    };

    // Fetch all users
    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/users/all`, {
                withCredentials: true
            });
            if (response.data.success) {
                setUsers(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            alert('Failed to fetch users');
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const validate = () => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        if (!editMode) {
            if (!formData.password) {
                newErrors.password = 'Password is required';
            } else if (formData.password.length < 6) {
                newErrors.password = 'Password must be at least 6 characters';
            }
        }

        if (!formData.role) {
            newErrors.role = 'Please select a role';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        setLoading(true);

        try {
            const endpoint = editMode
                ? `${API_URL}/admin/users/update/${currentUserId}`
                : `${API_URL}/admin/users/create`;

            const method = editMode ? 'put' : 'post';

            const response = await axios[method](endpoint, formData, {
                withCredentials: true
            });

            if (response.data.success) {
                // alert(response.data.message); // Removed alert for smoother UI
                setShowModal(false);
                resetForm();
                fetchUsers();
            } else {
                alert(response.data.message);
            }
        } catch (error) {
            console.error('Error:', error);
            alert(error.response?.data?.message || 'Operation failed');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (user) => {
        setEditMode(true);
        setCurrentUserId(user._id);
        setFormData({
            name: user.name,
            email: user.email,
            role: user.role,
            password: ''
        });
        setShowModal(true);
    };

    const handleDelete = async (userId, userName) => {
        if (!window.confirm(`Are you sure you want to delete ${userName}?`)) return;

        try {
            const response = await axios.delete(
                `${API_URL}/admin/users/delete/${userId}`,
                { withCredentials: true }
            );

            if (response.data.success) {
                fetchUsers();
            } else {
                alert(response.data.message);
            }
        } catch (error) {
            alert(error.response?.data?.message || 'Failed to delete user');
        }
    };

    const toggleUserStatus = async (userId, currentStatus) => {
        try {
            const response = await axios.put(
                `${API_URL}/admin/users/update/${userId}`,
                { isActive: !currentStatus },
                { withCredentials: true }
            );

            if (response.data.success) {
                fetchUsers();
            }
        } catch (error) {
            alert('Failed to update user status');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            password: '',
            role: 'hr_manager'
        });
        setErrors({});
        setEditMode(false);
        setCurrentUserId(null);
    };

    const openCreateModal = () => {
        resetForm();
        setShowModal(true);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
                    <p className="text-sm text-gray-500 mt-1">Create and manage system users with different roles</p>
                </div>
                <button
                    className="flex items-center justify-center px-4 py-2.5 bg-primary text-white rounded-lg hover:shadow-lg hover:bg-primary-dark transition-all duration-200 font-medium"
                    onClick={openCreateModal}
                >
                    <FaPlus className="mr-2" />
                    Create New User
                </button>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Permissions</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {users.map((user) => {
                                const role = roleConfig[user.role];
                                const activePermissions = getActivePermissions(user.permissions);

                                return (
                                    <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <div className={`mr-3 p-2 rounded-lg ${role?.color ? role.color.split(' ')[1] + ' ' + role.color.split(' ')[0] : 'bg-gray-100 text-gray-500'}`}>
                                                    {role?.icon}
                                                </div>
                                                <span className="font-medium text-gray-900">{user.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{user.email}</td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${role?.color || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                                                {role?.label || user.role}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.isActive
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                                }`}>
                                                {user.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {activePermissions.length > 0
                                                ? activePermissions.slice(0, 3).join(', ') +
                                                (activePermissions.length > 3 ? '...' : '')
                                                : 'None'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    onClick={() => handleEdit(user)}
                                                    title="Edit"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    className={`p-2 rounded-lg transition-colors ${user.isActive ? 'text-orange-500 hover:bg-orange-50' : 'text-green-500 hover:bg-green-50'}`}
                                                    onClick={() => toggleUserStatus(user._id, user.isActive)}
                                                    title={user.isActive ? 'Deactivate' : 'Activate'}
                                                >
                                                    {user.isActive ? <FaToggleOn size={18} /> : <FaToggleOff size={18} />}
                                                </button>
                                                <button
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    onClick={() => handleDelete(user._id, user.name)}
                                                    title="Delete"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {users.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                        No users found. Create your first user!
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-opacity">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h5 className="text-lg font-bold text-gray-900">
                                {editMode ? 'Edit User' : 'Create New User'}
                            </h5>
                            <button
                                type="button"
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                                onClick={() => {
                                    setShowModal(false);
                                    resetForm();
                                }}
                                disabled={loading}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="space-y-4">
                                {/* Name */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                                    <input
                                        type="text"
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter full name"
                                        disabled={loading}
                                    />
                                    {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Email *</label>
                                    <input
                                        type="email"
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="user@example.com"
                                        disabled={loading}
                                    />
                                    {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                                </div>

                                {/* Password */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Password {!editMode && '*'}
                                    </label>
                                    <input
                                        type="password"
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder={editMode ? 'Leave blank to keep current' : 'Min 6 characters'}
                                        disabled={loading}
                                    />
                                    {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
                                    {editMode && (
                                        <p className="mt-1 text-xs text-gray-500">Leave blank to keep current password</p>
                                    )}
                                </div>

                                {/* Role Selection */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Role *</label>
                                    <select
                                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none appearance-none bg-white ${errors.role ? 'border-red-500' : 'border-gray-300'}`}
                                        name="role"
                                        value={formData.role}
                                        onChange={handleChange}
                                        disabled={loading}
                                    >
                                        {Object.entries(roleConfig).map(([key, config]) => (
                                            <option key={key} value={key}>
                                                {config.label}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.role && <p className="mt-1 text-xs text-red-500">{errors.role}</p>}
                                </div>

                                {/* Role Info Card */}
                                {formData.role && (
                                    <div className={`p-4 rounded-lg border flex items-center gap-3 ${roleConfig[formData.role].color}`}>
                                        <div className="text-xl">
                                            {roleConfig[formData.role].icon}
                                        </div>
                                        <div>
                                            <p className="font-bold text-sm">{roleConfig[formData.role].label}</p>
                                            <p className="text-xs opacity-90">{roleConfig[formData.role].description}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-6 flex gap-3">
                                <button
                                    type="button"
                                    className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                                    onClick={() => {
                                        setShowModal(false);
                                        resetForm();
                                    }}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark font-medium shadow-lg hover:shadow-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            <span>Processing...</span>
                                        </div>
                                    ) : (
                                        editMode ? 'Update User' : 'Create User'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;